import React, { useEffect, useMemo, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';

import { useMailDrafts } from './MailDraftsContext';
import { getMembershipApplication, setMembershipApplicationStatus } from '../../../../services/membershipApplications';
import { sendMembershipMail } from '../../../../services/membershipmailsService';
import { pickBestTo, renderMailDraft } from '../../../../services/mailTemplatesService';

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export default function RejectMailModal({
  open,
  app,
  onClose,
  onChanged,
  previousStatus, // Estado anterior para revertir si se cancela
}) {
  const { rejectDraft } = useMailDrafts();

  // Render del draft con tokens -> valores por defecto
  const rendered = useMemo(() => renderMailDraft(rejectDraft || { subject: '', body: '' }, app), [rejectDraft, app]);
  const defaultTo = useMemo(() => pickBestTo(app), [app]);

  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(defaultTo || '');
  const [ccLine, setCcLine] = useState('');
  const [subject, setSubject] = useState(rendered.subject);
  const [body, setBody] = useState(rendered.body);
  const [copyToMe, setCopyToMe] = useState(true);

  useEffect(() => {
    if (!open) return;
    const { subject: s, body: b } = renderMailDraft(rejectDraft || { subject: '', body: '' }, app);
    setSubject(s);
    setBody(b);
    setTo(pickBestTo(app) || '');
    setCcLine('');
    setCopyToMe(true);
  }, [open, rejectDraft, app]);

  if (!open || !app?.id) return null;

  const send = async () => {
    if (!to || !emailRx.test(to)) {
      Swal.fire('Correo inválido', 'Completa un destinatario válido.', 'warning');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      Swal.fire('Faltan datos', 'Completa asunto y cuerpo del correo.', 'warning');
      return;
    }

    // Obtener nombre de la empresa para el mensaje de confirmación
    const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';

    // Confirmar antes de enviar
    const confirmResult = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${to.trim()}</strong> para la empresa <strong>${companyName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló
    }

    try {
      setSending(true);

      await sendMembershipMail(app.id, {
        template: 'REJECTED',
        to: to.trim(),
        cc: ccLine,                 // se normaliza en el service
        subject: subject.trim(),
        body: body.trim(),
        copyToMe: !!copyToMe,
      });

      // Actualizar estado
      const statusResp = await setMembershipApplicationStatus(app.id, 'REJECTED');
      const updated = statusResp?.data || statusResp || null;

      // Obtener nombre de la empresa
      const companyName = updated?.razonSocial || updated?.nombreFantasia || app?.razonSocial || app?.nombreFantasia || 'Sin nombre';

      Swal.fire({
        title: 'Enviado',
        html: `Correo de rechazo "${subject.trim()}" enviado correctamente a ${to.trim()} para la empresa <strong>${companyName}</strong> y estado actualizado.`,
        icon: 'success'
      });
      onChanged?.(updated || app);
      onClose?.();
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo completar el envío.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm flex items-center justify-center p-2 sm:p-3">
      <div className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-[#0b3a66] via-[#0b6b93] to-[#0488AF] text-white flex items-center justify-between">
          <div className="font-semibold">Enviar notificación de rechazo</div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm">
            <span className="text-base leading-none">ℹ️</span>
            <span>
              El estado de la postulación cambiará a <span className="font-bold text-blue-900">"Rechazada"</span> después del envío del correo.
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">Para</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="correo@empresa.cl"
              />
            </label>
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">CC (opcional)</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={ccLine}
                onChange={(e) => setCcLine(e.target.value)}
                placeholder="cc1@dom.com, cc2@dom.com"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Asunto</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Asunto del correo"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Cuerpo</div>
              <textarea
                className="w-full rounded-lg border px-3 py-2 text-sm"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
          </div>

          {/* Opciones */}
          <div className="rounded-xl border p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-2">Opciones</div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={copyToMe}
                onChange={(e) => setCopyToMe(e.target.checked)}
              />
              <span className="text-sm text-gray-700">Con copia al correo por defecto</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex justify-end gap-2 border-t bg-gray-50 flex-none">
          <button
            onClick={async () => {
              // Obtener nombre de la empresa
              const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';
              
              const result = await Swal.fire({
                title: '¿Cancelar envío?',
                html: `Si cancelas, <strong>no se enviará el correo</strong> y el estado de la postulación de la empresa <strong>${companyName}</strong> volverá a <strong>Pendiente</strong>.`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#6b7280',
                cancelButtonColor: '#0488AF',
                confirmButtonText: 'Sí, cancelar',
                cancelButtonText: 'Continuar enviando',
              });
              
              if (result.isConfirmed) {
                // Si hay un estado anterior, revertir el estado antes de cerrar
                if (previousStatus && app?.id) {
                  try {
                    // Obtener nombre de la empresa para los mensajes
                    const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';
                    
                    // Mostrar mensaje de carga mientras se regeneran los documentos
                    const loadingSwal = Swal.fire({
                      title: 'Regenerando documentos...',
                      html: `Por favor espera mientras se revierte el estado y se regeneran los documentos (PDF de ficha y declaración jurada) para la empresa <strong>${companyName}</strong>.`,
                      icon: 'info',
                      allowOutsideClick: false,
                      allowEscapeKey: false,
                      showConfirmButton: false,
                      didOpen: () => {
                        Swal.showLoading();
                      }
                    });

                    // Revertir el estado (el backend regenerará automáticamente los documentos cuando cambie a RECEIVED)
                    // El backend ya se encarga de regenerar el PDF de ficha y el PDF de declaración jurada (si hay valor de membresía)
                    await setMembershipApplicationStatus(app.id, previousStatus);
                    
                    // Esperar un momento para que el backend termine de regenerar los documentos
                    // El backend regenera automáticamente cuando cambia a RECEIVED
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    
                    // Recargar la aplicación para obtener los documentos regenerados por el backend
                    const finalApp = await getMembershipApplication(app.id);
                    const final = finalApp?.data || finalApp || app;
                    
                    // Cerrar el mensaje de carga
                    Swal.close();
                    
                    // Obtener nombre de la empresa para el mensaje de éxito
                    const finalCompanyName = final?.razonSocial || final?.nombreFantasia || app?.razonSocial || app?.nombreFantasia || companyName || 'Sin nombre';
                    
                    // Mostrar mensaje de éxito
                    await Swal.fire({
                      title: 'Estado revertido',
                      html: `El estado de la postulación de la empresa <strong>${finalCompanyName}</strong> ha vuelto a <strong>Pendiente</strong> y los documentos han sido regenerados.`,
                      icon: 'success',
                      timer: 2000,
                      showConfirmButton: false
                    });
                    
                    onChanged?.(final);
                  } catch (e) {
                    Swal.close();
                    console.error('Error al revertir estado:', e);
                    Swal.fire('Error', e?.message || 'No se pudo revertir el estado y regenerar los documentos.', 'error');
                  }
                }
                onClose?.();
              }
            }}
            className="h-10 px-4 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={send}
            disabled={sending}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2 text-sm"
          >
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Enviar correo de rechazo
          </button>
        </div>
      </div>
    </div>
  );
}
